// Firebase imports
import { 
  collection,
  addDoc,
  getDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError, toDRXError } from '@/lib/drx-error';
import { assertClientMayWriteCollection } from '@/lib/firestore-client-write-guard';
import {
  ISubscriptionRepository,
  type SubscriptionPlan,
} from './interfaces/subscription.repository.interface';

// Stripe SDK (mock implementation - replace with actual Stripe SDK)
class StripeMock {
  customers = {
    create: async (data: any) => ({
      id: `cus_${Date.now()}`,
      email: data.email,
      name: data.name,
      metadata: data.metadata || {}
    }),
    retrieve: async (id: string) => ({
      id,
      email: 'user@example.com',
      name: 'User Name',
      metadata: {}
    }),
    update: async (id: string, data: any) => ({
      id,
      ...data
    })
  };

  subscriptions = {
    create: async (data: any) => ({
      id: `sub_${Date.now()}`,
      customer: data.customer,
      status: 'active',
      current_period_start: Date.now() / 1000,
      current_period_end: (Date.now() + 30 * 24 * 60 * 60 * 1000) / 1000,
      cancel_at_period_end: false,
      items: {
        data: [{
          id: 'si_' + Date.now(),
          price: {
            id: data.price,
            product: 'prod_' + Date.now(),
            unit_amount: 2999,
            currency: 'usd'
          },
          quantity: 1
        }]
      },
      metadata: data.metadata || {}
    }),
    retrieve: async (id: string) => ({
      id,
      customer: 'cus_' + Date.now(),
      status: 'active',
      current_period_start: Date.now() / 1000,
      current_period_end: (Date.now() + 30 * 24 * 60 * 60 * 1000) / 1000,
      cancel_at_period_end: false,
      items: { data: [] },
      metadata: {}
    }),
    update: async (id: string, data: any) => ({
      id,
      ...data
    }),
    cancel: async (id: string, data: any = {}) => ({
      id,
      status: 'canceled',
      cancel_at_period_end: data.cancel_at_period_end || false
    })
  };

  prices = {
    list: async () => ({
      data: [
        {
          id: 'priceFree',
          product: 'prodFree',
          unit_amount: 0,
          currency: 'usd',
          metadata: { tier: 'free' }
        },
        {
          id: 'priceNobility',
          product: 'prodNobility',
          unit_amount: 1999,
          currency: 'usd',
          recurring: { interval: '6-months', intervalCount: 1 },
          metadata: { tier: 'nobility' }
        },
        {
          id: 'priceRoyalty',
          product: 'prodRoyalty',
          unit_amount: 4999,
          currency: 'usd',
          recurring: { interval: '6-months', intervalCount: 1 },
          metadata: { tier: 'royalty' }
        },
        {
          id: 'priceImperial',
          product: 'prodImperial',
          unit_amount: 9999,
          currency: 'usd',
          recurring: { interval: '6-months', intervalCount: 1 },
          metadata: { tier: 'imperial' }
        }
      ]
    })
  };
}

const stripe = new StripeMock();

export class SubscriptionRepository implements ISubscriptionRepository {
  constructor(private db: any) {
    console.log('[SubscriptionRepository] Constructor - Injected Firebase Admin DB instance:', !!this.db);
  }

  private collectionName = 'subscriptions';
  private plansCollection = 'subscriptionPlans';

  private toRepositoryError(error: unknown, code: string, devMessage: string) {
    return toDRXError(error, {
      code,
      devMessage,
    });
  }

  /** Browser cannot mutate `subscriptions/*` (see `firestore.rules`, `assertClientMayWriteCollection`). */
  private denyClientSubscriptionMutation(method: string): never {
    assertClientMayWriteCollection(this.collectionName, method);
  }

  // ===== SUBSCRIPTION CRUD =====

  async createSubscription(_subscriptionData: import('./interfaces/subscription.repository.interface').CreateSubscriptionData): Promise<import('./interfaces/subscription.repository.interface').GetSubscriptionData> {
    console.log('[SubscriptionRepository.createSubscription] (server-only; client cannot persist)');
    return this.denyClientSubscriptionMutation('createSubscription');
  }

  async getSubscriptionById(subscriptionId: string): Promise<import('./interfaces/subscription.repository.interface').GetSubscriptionData | null> {
    console.log('[SubscriptionRepository.getSubscriptionById] START - SubscriptionId:', subscriptionId);
    try {
      console.log('[SubscriptionRepository.getSubscriptionById] Fetching subscription document from Firestore');
      const docRef = doc(db, this.collectionName, subscriptionId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const subscription = {
          id: docSnap.id,
          ...docSnap.data(),
        } as import('./interfaces/subscription.repository.interface').GetSubscriptionData;
        console.log('[SubscriptionRepository.getSubscriptionById] SUCCESS - Found subscription:', subscription.tier);
        return subscription;
      }

      console.log('[SubscriptionRepository.getSubscriptionById] Subscription not found');
      return null;
    } catch (error) {
      console.error('[SubscriptionRepository.getSubscriptionById] ERROR:', error);
      throw this.toRepositoryError(error, 'SUBSCRIPTION_FETCH_FAILED', `Failed to get subscription ${subscriptionId}`);
    }
  }

  async getSubscriptionByUserId(userId: string): Promise<import('./interfaces/subscription.repository.interface').GetSubscriptionData | null> {
    console.log('[SubscriptionRepository.getSubscriptionByUserId] START - UserId:', userId);
    try {
      console.log('[SubscriptionRepository.getSubscriptionByUserId] Querying active subscription for user (simplified query)');
      // Use a simpler query to avoid composite index requirement
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        limit(10) // Get more docs and filter client-side
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        // Filter for active subscriptions client-side and get the most recent
        const activeSubscriptions = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
          } as import('./interfaces/subscription.repository.interface').GetSubscriptionData))
          .filter(sub => ['active', 'trialing'].includes(sub.status))
          .sort((a, b) => {
            // Sort by createdAt descending (most recent first)
            const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
            const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
            return bTime - aTime;
          });

        if (activeSubscriptions.length > 0) {
          const subscription = activeSubscriptions[0];
          console.log('[SubscriptionRepository.getSubscriptionByUserId] SUCCESS - Found subscription:', subscription.tier);
          return subscription;
        }
      }

      console.log('[SubscriptionRepository.getSubscriptionByUserId] No active subscription found for user');
      return null;
    } catch (error) {
      console.error('[SubscriptionRepository.getSubscriptionByUserId] ERROR:', error);
      throw this.toRepositoryError(error, 'SUBSCRIPTION_BY_USER_FETCH_FAILED', `Failed to get subscription for user ${userId}`);
    }
  }

  async updateSubscription(subscriptionId: string, updateData: import('./interfaces/subscription.repository.interface').UpdateSubscriptionData): Promise<import('./interfaces/subscription.repository.interface').GetSubscriptionData> {
    console.log('[SubscriptionRepository.updateSubscription] START - SubscriptionId:', subscriptionId, 'UpdateData:', updateData);
    return this.denyClientSubscriptionMutation('updateSubscription');
  }

  async deleteSubscription(subscriptionId: string): Promise<void> {
    console.log('[SubscriptionRepository.deleteSubscription] START - SubscriptionId:', subscriptionId);
    this.denyClientSubscriptionMutation('deleteSubscription');
  }

  // ===== STRIPE INTEGRATION =====

  async createStripeCustomer(customerData: { email: string; name?: string; metadata?: Record<string, any> }): Promise<import('./interfaces/subscription.repository.interface').StripeCustomerData> {
    console.log('[SubscriptionRepository.createStripeCustomer] START - CustomerData:', customerData);
    try {
      console.log('[SubscriptionRepository.createStripeCustomer] Creating Stripe customer');
      const customer = await stripe.customers.create({
        email: customerData.email,
        name: customerData.name,
        metadata: customerData.metadata
      });

      const result = customer as import('./interfaces/subscription.repository.interface').StripeCustomerData;
      console.log('[SubscriptionRepository.createStripeCustomer] SUCCESS - Created customer with ID:', result.id);
      return result;
    } catch (error) {
      console.error('[SubscriptionRepository.createStripeCustomer] ERROR:', error);
      throw this.toRepositoryError(error, 'STRIPE_CUSTOMER_CREATE_FAILED', 'Failed to create Stripe customer');
    }
  }

  async getStripeCustomer(customerId: string): Promise<import('./interfaces/subscription.repository.interface').StripeCustomerData | null> {
    try {
      const customer = await stripe.customers.retrieve(customerId);
      return customer as import('./interfaces/subscription.repository.interface').StripeCustomerData;
    } catch (error) {
      console.error('[SubscriptionRepository.getStripeCustomer] ERROR:', error);
      return null;
    }
  }

  async updateStripeCustomer(customerId: string, updateData: Partial<import('./interfaces/subscription.repository.interface').StripeCustomerData>): Promise<import('./interfaces/subscription.repository.interface').StripeCustomerData> {
    try {
      const customer = await stripe.customers.update(customerId, updateData);
      return customer as import('./interfaces/subscription.repository.interface').StripeCustomerData;
    } catch (error) {
      console.error('[SubscriptionRepository.updateStripeCustomer] ERROR:', error);
      throw this.toRepositoryError(error, 'STRIPE_CUSTOMER_UPDATE_FAILED', `Failed to update Stripe customer ${customerId}`);
    }
  }

  async createStripeSubscription(subscriptionData: {
    customer: string;
    price: string;
    metadata?: Record<string, any>;
  }): Promise<import('./interfaces/subscription.repository.interface').StripeSubscriptionData> {
    console.log('[SubscriptionRepository.createStripeSubscription] START - SubscriptionData:', subscriptionData);
    try {
      console.log('[SubscriptionRepository.createStripeSubscription] Creating Stripe subscription');
      const subscription = await stripe.subscriptions.create({
        customer: subscriptionData.customer,
        items: [{ price: subscriptionData.price }],
        metadata: subscriptionData.metadata
      });

      const result = subscription as import('./interfaces/subscription.repository.interface').StripeSubscriptionData;
      console.log('[SubscriptionRepository.createStripeSubscription] SUCCESS - Created subscription with ID:', result.id);
      return result;
    } catch (error) {
      console.error('[SubscriptionRepository.createStripeSubscription] ERROR:', error);
      throw this.toRepositoryError(error, 'STRIPE_SUBSCRIPTION_CREATE_FAILED', 'Failed to create Stripe subscription');
    }
  }

  async getStripeSubscription(subscriptionId: string): Promise<import('./interfaces/subscription.repository.interface').StripeSubscriptionData | null> {
    console.log('[SubscriptionRepository.getStripeSubscription] START - SubscriptionId:', subscriptionId);
    try {
      console.log('[SubscriptionRepository.getStripeSubscription] Retrieving Stripe subscription');
      const subscription = await stripe.subscriptions.retrieve(subscriptionId);
      const result = subscription as import('./interfaces/subscription.repository.interface').StripeSubscriptionData;
      console.log('[SubscriptionRepository.getStripeSubscription] SUCCESS - Retrieved subscription status:', result.status);
      return result;
    } catch (error) {
      console.error('[SubscriptionRepository.getStripeSubscription] ERROR:', error);
      return null;
    }
  }

  async updateStripeSubscription(subscriptionId: string, updateData: any): Promise<import('./interfaces/subscription.repository.interface').StripeSubscriptionData> {
    console.log('[SubscriptionRepository.updateStripeSubscription] START - SubscriptionId:', subscriptionId, 'UpdateData:', updateData);
    try {
      console.log('[SubscriptionRepository.updateStripeSubscription] Updating Stripe subscription');
      const subscription = await stripe.subscriptions.update(subscriptionId, updateData);
      const result = subscription as import('./interfaces/subscription.repository.interface').StripeSubscriptionData;
      console.log('[SubscriptionRepository.updateStripeSubscription] SUCCESS - Updated subscription:', result.id);
      return result;
    } catch (error) {
      console.error('[SubscriptionRepository.updateStripeSubscription] ERROR:', error);
      throw this.toRepositoryError(error, 'STRIPE_SUBSCRIPTION_UPDATE_FAILED', `Failed to update Stripe subscription ${subscriptionId}`);
    }
  }

  async cancelStripeSubscription(subscriptionId: string, cancelAtPeriodEnd?: boolean): Promise<import('./interfaces/subscription.repository.interface').StripeSubscriptionData> {
    console.log('[SubscriptionRepository.cancelStripeSubscription] START - SubscriptionId:', subscriptionId, 'CancelAtPeriodEnd:', cancelAtPeriodEnd);
    try {
      console.log('[SubscriptionRepository.cancelStripeSubscription] Canceling Stripe subscription');
      const subscription = await stripe.subscriptions.cancel(subscriptionId, {
        cancel_at_period_end: cancelAtPeriodEnd
      });
      const result = subscription as import('./interfaces/subscription.repository.interface').StripeSubscriptionData;
      console.log('[SubscriptionRepository.cancelStripeSubscription] SUCCESS - Canceled subscription status:', result.status);
      return result;
    } catch (error) {
      console.error('[SubscriptionRepository.cancelStripeSubscription] ERROR:', error);
      throw this.toRepositoryError(error, 'STRIPE_SUBSCRIPTION_CANCEL_FAILED', `Failed to cancel Stripe subscription ${subscriptionId}`);
    }
  }

  // ===== SYNC METHODS =====

  async syncStripeToDB(_stripeSubscription: import('./interfaces/subscription.repository.interface').StripeSubscriptionData): Promise<import('./interfaces/subscription.repository.interface').GetSubscriptionData> {
    console.log('[SubscriptionRepository.syncStripeToDB] (server-only)');
    return this.denyClientSubscriptionMutation('syncStripeToDB');
  }

  async syncDBToStripe(subscriptionId: string): Promise<import('./interfaces/subscription.repository.interface').StripeSubscriptionData> {
    console.log('[SubscriptionRepository.syncDBToStripe] START - SubscriptionId:', subscriptionId);
    try {
      console.log('[SubscriptionRepository.syncDBToStripe] Syncing database subscription to Stripe');
      const subscription = await this.getSubscriptionById(subscriptionId);
      if (!subscription?.stripeSubscriptionId) {
        throw new DRXError({
          code: 'SUBSCRIPTION_STRIPE_LINK_MISSING',
          devMessage: `Subscription ${subscriptionId} is not linked to Stripe`,
          userMessage: 'Subscription is not linked to Stripe.',
          level: 'warning',
        });
      }

      const result = await this.getStripeSubscription(subscription.stripeSubscriptionId) as import('./interfaces/subscription.repository.interface').StripeSubscriptionData;
      console.log('[SubscriptionRepository.syncDBToStripe] SUCCESS - Synced to Stripe');
      return result;
    } catch (error) {
      console.error('[SubscriptionRepository.syncDBToStripe] ERROR:', error);
      throw this.toRepositoryError(error, 'SUBSCRIPTION_DB_SYNC_FAILED', `Failed to sync subscription ${subscriptionId} to Stripe`);
    }
  }

  // ===== PLAN MANAGEMENT =====

  async getStripePricePlans(): Promise<import('./interfaces/subscription.repository.interface').SubscriptionPlan[]> {
    console.log('[SubscriptionRepository.getStripePricePlans] START - Getting all subscription plans from Stripe mock');
    try {
      console.log('[SubscriptionRepository.getStripePricePlans] Fetching plans from Stripe');
      // Get plans from Stripe
      const prices = await stripe.prices.list();

      const plans = prices.data.map((price) => {
        const rawTier = String(price.metadata?.tier ?? '').toLowerCase();
        const tier: SubscriptionPlan['tier'] =
          rawTier === 'nobility' ||
          rawTier === 'royalty' ||
          rawTier === 'imperial' ||
          rawTier === 'free'
            ? rawTier
            : 'free';
        return {
          id: price.id,
          name: `${tier} ${price.recurring?.interval || 'one-time'}`,
          tier,
          price: price.unit_amount || 0,
          currency: price.currency,
          interval: '6-months' as const,
          stripePriceId: price.id,
          features: [],
          popular: false,
        };
      });
      console.log('[SubscriptionRepository.getStripePricePlans] SUCCESS - Retrieved', plans.length, 'plans from Stripe');
      return plans;
    } catch (error) {
      console.error('[SubscriptionRepository.getStripePricePlans] ERROR:', error);
      // Return default plans
      console.log('[SubscriptionRepository.getStripePricePlans] Returning default plans due to error');
      return [
        {
          id: 'free',
          name: 'Free',
          tier: 'free',
          price: 0,
          currency: 'usd',
          interval: '6-months',
          stripePriceId: 'priceFree',
          features: ['Basic access', 'Limited courses'],
          popular: false
        },
        {
          id: 'nobility',
          name: 'Nobility',
          tier: 'nobility',
          price: 19.99,
          currency: 'usd',
          interval: '6-months',
          stripePriceId: 'priceNobility',
          features: ['Be booked', 'Priority support'],
          popular: false
        },
        {
          id: 'royalty',
          name: 'Royalty',
          tier: 'royalty',
          price: 49.99,
          currency: 'usd',
          interval: '6-months',
          stripePriceId: 'priceRoyalty',
          features: ['Sell courses', 'Analytics', 'Certification builder'],
          popular: true
        },
        {
          id: 'imperial',
          name: 'Imperial',
          tier: 'imperial',
          price: 99.99,
          currency: 'usd',
          interval: '6-months',
          stripePriceId: 'priceImperial',
          features: ['White label', 'Advanced analytics', 'API access'],
          popular: false
        }
      ];
    }
  }

  async getPlanById(planId: string): Promise<import('./interfaces/subscription.repository.interface').SubscriptionPlan | null> {
    console.log('[SubscriptionRepository.getPlanById] START - PlanId:', planId);
    try {
      console.log('[SubscriptionRepository.getPlanById] Finding plan by ID');
      const plans = await this.getSubscriptionPlans();
      const plan = plans.find(plan => plan.id === planId) || null;
      console.log('[SubscriptionRepository.getPlanById] SUCCESS - Found plan:', plan ? plan.name : 'Not found');
      return plan;
    } catch (error) {
      console.error('[SubscriptionRepository.getPlanById] ERROR:', error);
      return null;
    }
  }

  async getPlanByTier(tier: string): Promise<import('./interfaces/subscription.repository.interface').SubscriptionPlan | null> {
    console.log('[SubscriptionRepository.getPlanByTier] START - Tier:', tier);
    try {
      console.log('[SubscriptionRepository.getPlanByTier] Finding plan by tier');
      const plans = await this.getSubscriptionPlans();
      const plan = plans.find(plan => plan.tier === tier) || null;
      console.log('[SubscriptionRepository.getPlanByTier] SUCCESS - Found plan:', plan ? plan.name : 'Not found');
      return plan;
    } catch (error) {
      console.error('[SubscriptionRepository.getPlanByTier] ERROR:', error);
      return null;
    }
  }

  // ===== USER SUBSCRIPTION METHODS =====

  async getUserActiveSubscription(userId: string): Promise<import('./interfaces/subscription.repository.interface').GetSubscriptionData | null> {
    console.log('[SubscriptionRepository.getUserActiveSubscription] START - UserId:', userId);
    console.log('[SubscriptionRepository.getUserActiveSubscription] Delegating to getSubscriptionByUserId');
    return this.getSubscriptionByUserId(userId);
  }

  async getUserSubscriptionHistory(userId: string): Promise<import('./interfaces/subscription.repository.interface').GetSubscriptionData[]> {
    console.log('[SubscriptionRepository.getUserSubscriptionHistory] START - UserId:', userId);
    try {
      console.log('[SubscriptionRepository.getUserSubscriptionHistory] Querying all subscriptions for user');
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const subscriptions: import('./interfaces/subscription.repository.interface').GetSubscriptionData[] = [];

      querySnapshot.forEach((doc) => {
        subscriptions.push({
          id: doc.id,
          ...doc.data(),
        } as import('./interfaces/subscription.repository.interface').GetSubscriptionData);
      });

      console.log('[SubscriptionRepository.getUserSubscriptionHistory] SUCCESS - Retrieved', subscriptions.length, 'subscriptions for user');
      return subscriptions;
    } catch (error) {
      console.error('[SubscriptionRepository.getUserSubscriptionHistory] ERROR:', error);
      throw this.toRepositoryError(error, 'SUBSCRIPTION_HISTORY_FETCH_FAILED', `Failed to get subscription history for user ${userId}`);
    }
  }

  // Get all subscriptions (admin only)
  async getAllSubscriptions(limitCount?: number): Promise<import('./interfaces/subscription.repository.interface').GetSubscriptionData[]> {
    console.log('[SubscriptionRepository.getAllSubscriptions] START - LimitCount:', limitCount);
    try {
      console.log('[SubscriptionRepository.getAllSubscriptions] Querying all subscriptions (admin access)');
      let q = query(
        collection(db, this.collectionName),
        orderBy('createdAt', 'desc')
      );

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      const subscriptions: import('./interfaces/subscription.repository.interface').GetSubscriptionData[] = [];

      querySnapshot.forEach((doc) => {
        subscriptions.push({
          id: doc.id,
          ...doc.data(),
        } as import('./interfaces/subscription.repository.interface').GetSubscriptionData);
      });

      console.log('[SubscriptionRepository.getAllSubscriptions] SUCCESS - Retrieved', subscriptions.length, 'subscriptions');
      return subscriptions;
    } catch (error) {
      console.error('[SubscriptionRepository.getAllSubscriptions] ERROR:', error);
      throw this.toRepositoryError(error, 'SUBSCRIPTIONS_ALL_FETCH_FAILED', 'Failed to get all subscriptions');
    }
  }

  async checkUserSubscriptionStatus(userId: string): Promise<{
    hasActiveSubscription: boolean;
    tier: string;
    status: string;
    daysUntilExpiry: number;
  }> {
    console.log('[SubscriptionRepository.checkUserSubscriptionStatus] START - UserId:', userId);
    try {
      console.log('[SubscriptionRepository.checkUserSubscriptionStatus] Checking subscription status for user');
      const subscription = await this.getSubscriptionByUserId(userId);

      if (!subscription) {
        console.log('[SubscriptionRepository.checkUserSubscriptionStatus] No subscription found - returning free tier status');
        return {
          hasActiveSubscription: false,
          tier: 'free',
          status: 'none',
          daysUntilExpiry: 0
        };
      }

      console.log('[SubscriptionRepository.checkUserSubscriptionStatus] Found subscription - calculating status');
      const now = new Date();
      const endDate = subscription.current_period_end?.toDate?.() || new Date();
      const daysUntilExpiry = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

      const result = {
        hasActiveSubscription: subscription.status === 'active' || subscription.status === 'trialing',
        tier: subscription.tier,
        status: subscription.status,
        daysUntilExpiry
      };
      console.log('[SubscriptionRepository.checkUserSubscriptionStatus] SUCCESS - Status:', result);
      return result;
    } catch (error) {
      console.error('[SubscriptionRepository.checkUserSubscriptionStatus] ERROR:', error);
      console.log('[SubscriptionRepository.checkUserSubscriptionStatus] Returning error status');
      return {
        hasActiveSubscription: false,
        tier: 'free',
        status: 'error',
        daysUntilExpiry: 0
      };
    }
  }

  // ===== BILLING METHODS =====

  async processSubscriptionPayment(subscriptionId: string): Promise<any> {
    console.log('[SubscriptionRepository.processSubscriptionPayment] START - SubscriptionId:', subscriptionId);
    console.log('[SubscriptionRepository.processSubscriptionPayment] Processing mock payment');
    // Mock payment processing - would integrate with payment processor
    const result = {
      success: true,
      transactionId: `txn_${Date.now()}`,
      amount: 49.99,
      currency: 'usd',
      processedAt: new Date()
    };
    console.log('[SubscriptionRepository.processSubscriptionPayment] SUCCESS - Processed payment:', result.transactionId);
    return result;
  }

  async handleSubscriptionWebhook(webhookData: any): Promise<void> {
    console.log('[SubscriptionRepository.handleSubscriptionWebhook] START - WebhookType:', webhookData.type);
    try {
      console.log('[SubscriptionRepository.handleSubscriptionWebhook] Processing webhook event');
      // Handle different webhook events
      switch (webhookData.type) {
        case 'customer.subscription.created':
          console.log('[SubscriptionRepository.handleSubscriptionWebhook] Handling subscription created');
          await this.syncStripeToDB(webhookData.data.object);
          break;
        case 'customer.subscription.updated':
          console.log('[SubscriptionRepository.handleSubscriptionWebhook] Handling subscription updated');
          await this.syncStripeToDB(webhookData.data.object);
          break;
        case 'customer.subscription.deleted':
          console.log('[SubscriptionRepository.handleSubscriptionWebhook] Handling subscription deleted');
          // Mark as canceled in DB
          break;
        default:
          console.log('[SubscriptionRepository.handleSubscriptionWebhook] Unhandled webhook event:', webhookData.type);
      }
      console.log('[SubscriptionRepository.handleSubscriptionWebhook] SUCCESS - Processed webhook');
    } catch (error) {
      console.error('[SubscriptionRepository.handleSubscriptionWebhook] ERROR:', error);
      throw this.toRepositoryError(error, 'SUBSCRIPTION_WEBHOOK_HANDLE_FAILED', `Failed to handle subscription webhook ${webhookData?.type ?? 'unknown'}`);
    }
  }

  // ===== ANALYTICS =====

  async getSubscriptionMetrics(dateRange?: { start: Date; end: Date }): Promise<{
    totalSubscriptions: number;
    activeSubscriptions: number;
    canceledSubscriptions: number;
    revenue: number;
    churnRate: number;
    popularPlans: Array<{ planId: string; count: number }>;
  }> {
    console.log('[SubscriptionRepository.getSubscriptionMetrics] START - DateRange:', dateRange);
    try {
      console.log('[SubscriptionRepository.getSubscriptionMetrics] Calculating subscription metrics');
      const collectionRef = collection(db, this.collectionName);
      const q = dateRange
        ? query(
            collectionRef,
            where('createdAt', '>=', dateRange.start),
            where('createdAt', '<=', dateRange.end)
          )
        : query(collectionRef);

      const querySnapshot = await getDocs(q);
      const subscriptions = querySnapshot.docs.map(doc => doc.data());
      console.log('[SubscriptionRepository.getSubscriptionMetrics] Processing', subscriptions.length, 'subscription records');

      const totalSubscriptions = subscriptions.length;
      const activeSubscriptions = subscriptions.filter(s => s.status === 'active').length;
      const canceledSubscriptions = subscriptions.filter(s => s.status === 'canceled').length;

      // Mock additional metrics
      const revenue = subscriptions.reduce((sum, s) => sum + (s.amount || 0), 0);
      const churnRate = totalSubscriptions > 0 ? (canceledSubscriptions / totalSubscriptions) * 100 : 0;

      // Count plans
      const planCounts: Record<string, number> = {};
      subscriptions.forEach(s => {
        const tier = s.tier || 'free';
        planCounts[tier] = (planCounts[tier] || 0) + 1;
      });

      const popularPlans = Object.entries(planCounts)
        .map(([planId, count]) => ({ planId, count }))
        .sort((a, b) => b.count - a.count);

      const result = {
        totalSubscriptions,
        activeSubscriptions,
        canceledSubscriptions,
        revenue,
        churnRate,
        popularPlans
      };
      console.log('[SubscriptionRepository.getSubscriptionMetrics] SUCCESS - Calculated metrics:', result);
      return result;
    } catch (error) {
      console.error('[SubscriptionRepository.getSubscriptionMetrics] ERROR:', error);
      throw this.toRepositoryError(error, 'SUBSCRIPTION_METRICS_FETCH_FAILED', 'Failed to get subscription metrics');
    }
  }

  // ===== ADMIN / LIFECYCLE METHODS (absorbed from firestore.ts) =====

  async cancelUserSubscription(_subscriptionId: string, _reason?: string, _immediate?: boolean): Promise<any> {
    return this.denyClientSubscriptionMutation('cancelUserSubscription');
  }

  async reactivateUserSubscription(_subscriptionId: string): Promise<any> {
    return this.denyClientSubscriptionMutation('reactivateUserSubscription');
  }

  async getAllUserSubscriptions(): Promise<any[]> {
    try {
      const snapshot = await getDocs(collection(db, 'subscriptions'));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      throw new DRXError({
        code: 'SUBSCRIPTION_ALL_FETCH_FAILED',
        devMessage: 'Failed to fetch all user subscriptions',
        cause: error,
      });
    }
  }

  async updateSubscriptionStatus(_sessionId: string, _status: string): Promise<{ id: string; status: string }> {
    return this.denyClientSubscriptionMutation('updateSubscriptionStatus');
  }

  // ===== SUBSCRIPTION PLAN ADMIN METHODS =====

  async getSubscriptionPlans(): Promise<any[]> {
    try {
      const q = query(collection(db, this.plansCollection), where('isActive', '==', true));
      const snapshot = await getDocs(q);
      const plans = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      return plans.sort((a: any, b: any) => {
        const getPrice = (plan: any) => {
          if (plan.tier === 'FREE' || plan.slug === 'free' || plan.name === 'Free') return 0;
          return Number(plan.price || 0);
        };
        return getPrice(a) - getPrice(b);
      });
    } catch (error) {
      throw new DRXError({
        code: 'SUBSCRIPTION_PLANS_FETCH_FAILED',
        devMessage: 'Failed to fetch subscription plans',
        cause: error,
      });
    }
  }

  async getAllSubscriptionPlans(): Promise<any[]> {
    try {
      const snapshot = await getDocs(collection(db, this.plansCollection));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      throw new DRXError({
        code: 'SUBSCRIPTION_PLANS_ALL_FETCH_FAILED',
        devMessage: 'Failed to fetch all subscription plans',
        cause: error,
      });
    }
  }

  async createSubscriptionPlan(planData: any): Promise<any> {
    try {
      const docRef = await addDoc(collection(db, this.plansCollection), {
        ...planData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { id: docRef.id, ...planData };
    } catch (error) {
      throw new DRXError({
        code: 'SUBSCRIPTION_PLAN_CREATE_FAILED',
        devMessage: 'Failed to create subscription plan',
        cause: error,
      });
    }
  }

  async updateSubscriptionPlan(planId: string, updates: any): Promise<any> {
    try {
      const docRef = doc(db, this.plansCollection, planId);
      await updateDoc(docRef, { ...updates, updatedAt: serverTimestamp() });
      return { id: planId, ...updates };
    } catch (error) {
      throw new DRXError({
        code: 'SUBSCRIPTION_PLAN_UPDATE_FAILED',
        devMessage: `Failed to update subscription plan ${planId}`,
        cause: error,
      });
    }
  }

  async deleteSubscriptionPlan(planId: string): Promise<{ success: boolean }> {
    try {
      const docRef = doc(db, this.plansCollection, planId);
      await deleteDoc(docRef);
      return { success: true };
    } catch (error) {
      throw new DRXError({
        code: 'SUBSCRIPTION_PLAN_DELETE_FAILED',
        devMessage: `Failed to delete subscription plan ${planId}`,
        cause: error,
      });
    }
  }

  // ===== SELLER ADMIN METHODS =====

  async getAllSellers(): Promise<any[]> {
    try {
      const q = query(collection(db, 'users'), where('roles', 'array-contains', 'SELLER'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      throw new DRXError({
        code: 'SELLERS_FETCH_FAILED',
        devMessage: 'Failed to fetch all sellers',
        cause: error,
      });
    }
  }

  async approveSeller(sellerId: string, reason?: string): Promise<{ id: string; sellerStatus: string }> {
    try {
      const docRef = doc(db, 'users', sellerId);
      await updateDoc(docRef, {
        sellerStatus: 'approved',
        sellerApprovedAt: serverTimestamp(),
        sellerApprovalReason: reason || 'Approved by admin',
        updatedAt: serverTimestamp(),
      });
      return { id: sellerId, sellerStatus: 'approved' };
    } catch (error) {
      throw new DRXError({
        code: 'SELLER_APPROVE_FAILED',
        devMessage: `Failed to approve seller ${sellerId}`,
        cause: error,
      });
    }
  }

  async rejectSeller(sellerId: string, reason?: string): Promise<{ id: string; sellerStatus: string }> {
    try {
      const docRef = doc(db, 'users', sellerId);
      await updateDoc(docRef, {
        sellerStatus: 'rejected',
        sellerRejectedAt: serverTimestamp(),
        sellerRejectionReason: reason || 'Rejected by admin',
        updatedAt: serverTimestamp(),
      });
      return { id: sellerId, sellerStatus: 'rejected' };
    } catch (error) {
      throw new DRXError({
        code: 'SELLER_REJECT_FAILED',
        devMessage: `Failed to reject seller ${sellerId}`,
        cause: error,
      });
    }
  }

  async getCurrentUserSubscription(userId: string): Promise<any | null> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('status', 'in', ['active', 'pastDue'])
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;

      const subscriptions = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      return subscriptions.sort((a: any, b: any) => {
        const aDate = a.createdAt?.toDate?.() || new Date(0);
        const bDate = b.createdAt?.toDate?.() || new Date(0);
        return bDate.getTime() - aDate.getTime();
      })[0];
    } catch (error) {
      throw new DRXError({
        code: 'SUBSCRIPTION_CURRENT_FETCH_FAILED',
        devMessage: `Failed to fetch current subscription for user ${userId}`,
        cause: error,
      });
    }
  }
}
